import apiClient from './client'
import { unwrapResponseData } from './response'
import type { AdminStats, AdminQuery, User, Event, Transaction, Notification, SystemConfig, Gift, RefundRequest } from '@/types/admin'

// Helper: unwrap BE response envelope { success, data } → actual data
function unwrap<T = any>(response: any): T {
  if (response && response.success === true && response.data !== undefined) {
    return response.data as T
  }
  if (response && typeof response === 'object' && !response.success && response.data !== undefined) {
    return response as T
  }
  return (response ?? {}) as T
}

export type { AdminStats, AdminQuery, User, Event, Transaction, Notification, SystemConfig, Gift, RefundRequest }

export const adminApi = {
  // Dashboard
  async getDashboardStats(): Promise<any> {
    const response = await apiClient.get('/admin/dashboard/stats')
    return response.data
  },

  async getRecentActivities(limit: number = 10): Promise<any> {
    const response = await apiClient.get('/admin/dashboard/recent-activities', {
      params: { limit }
    })
    return response.data
  },

  // Users Management
  async getUsers(query?: any): Promise<any> {
    const response = await apiClient.get('/admin/users', { params: query })
    const raw = response.data || {}
    // Normalize to { users, pagination }
    const users = Array.isArray(raw.users)
      ? raw.users
      : Array.isArray(raw.data?.users)
        ? raw.data.users
        : Array.isArray(raw.data)
          ? raw.data
          : []
    const pagination = raw.pagination
      || raw.data?.pagination
      || raw.meta?.pagination
      || { page: 1, limit: users.length || 0, total: users.length || 0, pages: 1 }
    return { users, pagination }
  },

  async getUser(id: string): Promise<any> {
    const response = await apiClient.get(`/admin/users/${id}`)
    return response.data
  },

  async updateUserStatus(id: string, status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED'): Promise<any> {
    const response = await apiClient.put(`/admin/users/${id}/status`, { status })
    return response.data
  },

  async updateUserRole(id: string, role: 'USER' | 'ORGANIZER' | 'ADMIN'): Promise<any> {
    const response = await apiClient.put(`/admin/users/${id}/role`, { role })
    return response.data
  },

  // Events Management
  async getEvents(query?: any): Promise<any> {
    const response = await apiClient.get('/admin/events', { params: query })
    const raw = response.data || {}
    const events = Array.isArray(raw.events)
      ? raw.events
      : Array.isArray(raw.data?.events)
        ? raw.data.events
        : Array.isArray(raw.data)
          ? raw.data
          : []
    const pagination = raw.pagination
      || raw.data?.pagination
      || raw.meta?.pagination
      || { page: 1, limit: events.length || 0, total: events.length || 0, pages: 1 }
    return { events, pagination }
  },

  async getEvent(id: string): Promise<any> {
    const response = await apiClient.get(`/admin/events/${id}`)
    return response.data
  },

  async updateEventStatus(id: string, status: 'DRAFT' | 'PENDING' | 'PUBLISHED' | 'CANCELLED'): Promise<any> {
    const response = await apiClient.put(`/admin/events/${id}/status`, { status })
    return response.data
  },

  async approveEvent(id: string): Promise<any> {
    const response = await apiClient.put(`/admin/events/${id}/approve`)
    return response.data
  },

  async rejectEvent(id: string, reason: string): Promise<any> {
    const response = await apiClient.put(`/admin/events/${id}/reject`, { reason })
    return response.data
  },

  async updateEvent(id: string, payload: Record<string, unknown>): Promise<any> {
    const response = await apiClient.put(`/admin/events/${id}`, payload)
    return unwrapResponseData(response)
  },

  async deleteEvent(id: string): Promise<{ message?: string }> {
    const response = await apiClient.delete(`/admin/events/${id}`)
    return unwrapResponseData(response)
  },

  // Transactions Management
  async getTransactions(query?: any): Promise<any> {
    const response = await apiClient.get('/admin/transactions', { params: query })
    const raw = response.data || {}
    const transactions = Array.isArray(raw.transactions)
      ? raw.transactions
      : Array.isArray(raw.data?.transactions)
        ? raw.data.transactions
        : Array.isArray(raw.data)
          ? raw.data
          : []
    const pagination = raw.pagination
      || raw.data?.pagination
      || raw.meta?.pagination
      || { page: 1, limit: transactions.length || 0, total: transactions.length || 0, pages: 1 }
    return { transactions, pagination }
  },

  async getTransaction(id: string): Promise<any> {
    const response = await apiClient.get(`/admin/transactions/${id}`)
    return response.data
  },

  async updateTransactionStatus(id: string, status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'CANCELLED'): Promise<any> {
    const response = await apiClient.put(`/admin/transactions/${id}/status`, { status })
    return response.data
  },

  // System Configuration
  async getSystemConfig(): Promise<any> {
    const response = await apiClient.get('/admin/system-config')
    return unwrap(response.data)
  },

  async updateSystemConfig(data: any): Promise<any> {
    const response = await apiClient.put('/admin/system-config', data)
    return unwrap(response.data)
  },

  async sendTestEmail(data: {
    smtpHost: string
    smtpPort: number
    secure?: boolean
    username: string
    password: string
    to: string
    message?: string
  }): Promise<any> {
    const response = await apiClient.post('/admin/system-config/email/test', data)
    return response.data
  },

  // Firebase Settings
  async getFirebaseSettings(): Promise<any> {
    const response = await apiClient.get('/admin/system-config/firebase')
    return unwrap(response.data)
  },

  async updateFirebaseSettings(data: {
    apiKey?: string
    authDomain?: string
    projectId?: string
    storageBucket?: string
    messagingSenderId?: string
    appId?: string
    measurementId?: string
  }): Promise<any> {
    const response = await apiClient.put('/admin/system-config/firebase', data)
    return unwrap(response.data)
  },

  // Notifications
  async getNotifications(query?: any): Promise<any> {
    const response = await apiClient.get('/admin/notifications', { params: query })
    const raw = response.data || {}
    const notifications = Array.isArray(raw.notifications)
      ? raw.notifications
      : Array.isArray(raw.data?.notifications)
        ? raw.data.notifications
        : Array.isArray(raw.data)
          ? raw.data
          : []
    const pagination = raw.pagination
      || raw.data?.pagination
      || raw.meta?.pagination
      || { page: 1, limit: notifications.length || 0, total: notifications.length || 0, pages: 1 }
    return { notifications, pagination }
  },

  async createNotification(data: any): Promise<any> {
    const response = await apiClient.post('/admin/notifications', data)
    return response.data
  },

  async updateNotification(id: string, data: any): Promise<any> {
    const response = await apiClient.put(`/admin/notifications/${id}`, data)
    return response.data
  },

  async deleteNotification(id: string): Promise<any> {
    const response = await apiClient.delete(`/admin/notifications/${id}`)
    return response.data
  },

  // Gifts Management
  async getGifts(query?: any): Promise<any> {
    const response = await apiClient.get('/admin/gifts', { params: query })
    const raw = response.data || {}
    const gifts = Array.isArray(raw.gifts)
      ? raw.gifts
      : Array.isArray(raw.data?.gifts)
        ? raw.data.gifts
        : Array.isArray(raw.data)
          ? raw.data
          : []
    const pagination = raw.pagination
      || raw.data?.pagination
      || raw.meta?.pagination
      || { page: 1, limit: gifts.length || 0, total: gifts.length || 0, pages: 1 }
    return { gifts, pagination }
  },

  async createGift(data: any): Promise<any> {
    const response = await apiClient.post('/admin/gifts', data)
    return response.data
  },

  async updateGift(id: string, data: any): Promise<any> {
    const response = await apiClient.put(`/admin/gifts/${id}`, data)
    return response.data
  },

  async deleteGift(id: string): Promise<any> {
    const response = await apiClient.delete(`/admin/gifts/${id}`)
    return response.data
  },

  // Reports
  async getEventsReport(query?: any): Promise<any> {
    const response = await apiClient.get('/admin/reports/events', { params: query })
    return response.data
  },

  async getTransactionsReport(query?: any): Promise<any> {
    const response = await apiClient.get('/admin/reports/transactions', { params: query })
    return response.data
  },

  async getUsersReport(query?: any): Promise<any> {
    const response = await apiClient.get('/admin/reports/users', { params: query })
    return response.data
  },

  // Admin Wallet Adjustments
  async adminDebitWallet(payload: { userId: string; amount: number; reason?: string }): Promise<any> {
    const response = await apiClient.post('/admin/payments/wallet/debit', payload)
    return response.data
  },

  async adminCreditWallet(payload: { userId: string; amount: number; reason?: string }): Promise<any> {
    const response = await apiClient.post('/admin/payments/wallet/credit', payload)
    return response.data
  }
}